import { inject, Injectable } from '@angular/core';

import { from, type Observable } from 'rxjs';

import { type PaginationMeta } from '@core/http-types';
import type { ID } from '@core/ngrx-legacy';
import type {
  AnyOption,
  CreateViewPayload,
  DataSource,
  DataSourceListItem,
  FetchGroupsPayload,
  FetchRecordsPayload,
  FilterPayload,
  GroupId,
  GroupsQueryParams,
  GroupsResponse,
  InputOptionsResponse,
  LayoutRecordsCountPayload,
  RecordsResponse,
  UpdateViewPayload,
  View,
  ViewListItem,
  ViewPositionPayload,
  ViewRecord,
} from '@core/views-types';

import { DefaultViewsHttpService } from './default-http-views.service';
import { ViewsHttpServiceI } from './view-data.types';
import type { FilterGroupResponse } from '../store/filter-group/filter-group.store';
import type { RecordsQueryParams } from '../store/records/records.store';
import type { FetchSwimlanesPayload } from '../store/swimlanes/swimlanes.store';
import type { ViewResponse } from '../store/view/view.store';
import type { ViewCounter } from '../store/view-counters/view-counters.store';

@Injectable({ providedIn: 'root' })
export class ViewsDataService implements ViewsHttpServiceI {
  private readonly defaultService = inject(DefaultViewsHttpService);
  private dynamicServices: Record<string, ViewsHttpServiceI | null> = {};

  public setDataSourceService(dataSourceName: string, service: ViewsHttpServiceI | null): void {
    this.dynamicServices = {
      ...this.dynamicServices,
      [dataSourceName]: service,
    };
  }

  public listDataSources(): Observable<DataSourceListItem[]> {
    return this.defaultService.listDataSources();
  }

  public fetchDataSource(dataSourceId: string): Observable<DataSource> {
    return from(this.resolveDataService(dataSourceId).fetchDataSource(dataSourceId));
  }

  public fetchFilterGroup(
    dataSourceId: string,
    filterGroupId: string,
    queryParams: { searchQuery?: string; page?: number; limit?: number },
  ): Observable<FilterGroupResponse> {
    return from(
      this.resolveDataService(dataSourceId).fetchFilterGroup(
        dataSourceId,
        filterGroupId,
        queryParams,
      ),
    );
  }

  public fetchInputOptions(
    dataSourceId: string,
    filterId: string,
    inputId: string,
    filterValue: FilterPayload | null,
    queryParams?: Partial<Omit<PaginationMeta, 'totalCount'> & { searchQuery: string }>,
  ): Observable<InputOptionsResponse<AnyOption>> {
    return from(
      this.resolveDataService(dataSourceId).fetchInputOptions(
        dataSourceId,
        filterId,
        inputId,
        filterValue,
        queryParams,
      ),
    );
  }

  public fetchViewsList(dataSourceId: string): Observable<ViewListItem[]> {
    return from(this.resolveDataService(dataSourceId).fetchViewsList(dataSourceId));
  }

  public updateViewsPositions(
    dataSourceId: string,
    positions: ViewPositionPayload[],
  ): Observable<ViewListItem[]> {
    return from(
      this.resolveDataService(dataSourceId).updateViewsPositions(dataSourceId, positions),
    );
  }

  public fetchView(dataSourceId: string, viewId: View['id']): Observable<ViewResponse> {
    return from(this.resolveDataService(dataSourceId).fetchView(dataSourceId, viewId));
  }

  public createView(dataSourceId: string, data: CreateViewPayload): Observable<ViewResponse> {
    return from(this.resolveDataService(dataSourceId).createView(dataSourceId, data));
  }

  public removeView(dataSourceId: string, viewId: number): Observable<ViewResponse> {
    return from(this.resolveDataService(dataSourceId).removeView(dataSourceId, viewId));
  }

  public updateView(
    dataSourceId: string,
    viewPayload: UpdateViewPayload,
  ): Observable<ViewResponse> {
    return from(this.resolveDataService(dataSourceId).updateView(dataSourceId, viewPayload));
  }

  public fetchGroups(
    dataSourceId: string,
    data: FetchGroupsPayload,
    queryParams?: GroupsQueryParams,
  ): Observable<GroupsResponse> {
    return from(this.resolveDataService(dataSourceId).fetchGroups(dataSourceId, data, queryParams));
  }

  public fetchSwimlanes(
    dataSourceId: string,
    data: FetchSwimlanesPayload,
    queryParams?: GroupsQueryParams,
  ) {
    return from(
      this.resolveDataService(dataSourceId).fetchSwimlanes(dataSourceId, data, queryParams),
    );
  }

  public fetchRecords(
    dataSourceId: string,
    data: FetchRecordsPayload,
    queryParams?: RecordsQueryParams,
  ): Observable<RecordsResponse> {
    return from(
      this.resolveDataService(dataSourceId).fetchRecords(dataSourceId, data, queryParams),
    );
  }

  public fetchLayoutRecordsCount(
    dataSourceId: string,
    data: LayoutRecordsCountPayload,
    queryParams?: RecordsQueryParams,
  ): Observable<RecordsResponse> {
    return from(
      this.resolveDataService(dataSourceId).fetchLayoutRecordsCount(
        dataSourceId,
        data,
        queryParams,
      ),
    );
  }

  public fetchViewCounters(dataSourceId: string, ids: ID[]): Observable<ViewCounter[]> {
    return from(this.resolveDataService(dataSourceId).fetchViewCounters(dataSourceId, ids));
  }

  public saveSnapshot(
    dataSourceId: string,
    viewId: View['id'],
    codename: View['codename'],
    form: CreateViewPayload,
  ): Observable<void> {
    return from(
      this.resolveDataService(dataSourceId).saveSnapshot(dataSourceId, viewId, codename, form),
    );
  }

  public clearSnapshot(
    dataSourceId: string,
    viewId: View['id'],
    codename: View['codename'],
  ): Observable<void> {
    return from(
      this.resolveDataService(dataSourceId).clearSnapshot(dataSourceId, viewId, codename),
    );
  }

  public moveRecord(
    dataSourceId: string,
    recordId: ViewRecord['id'],
    targetGroup: { configId: string; groupId: GroupId },
  ) {
    return from(
      this.resolveDataService(dataSourceId).moveRecord(dataSourceId, recordId, targetGroup),
    );
  }

  private resolveDataService(datasourceId: string) {
    return this.dynamicServices[datasourceId] ?? this.defaultService;
  }
}
